import { Axios } from 'axios';
import { config } from '../config';
import { HoundModel, QueueModel, RaceModel, HoundQueryDto, FindAllResponse, QueueQueryDto, RaceQueryDto, ArenaModel, ArenaQueryDto, PreferencesModel } from '../../../common';
import { loadingInstances } from "../utils/loading";
import { toast } from '../utils/notification.util';

import { maxTicketPrice, minTicketPrice } from "../components/race-tables/race-filters";
import { exchangeRates, exchangeRatesAvailable } from '../data/global';
import { EthersService as eth } from '.';
import { watch } from 'vue';

export class HttpService {
  axios: Axios;
  backgroundAxios: Axios;

  constructor() {
    watch(() => eth.reactive.value.network, (network) => {
      console.log(config.local);
      if (!network.chainId) {
        return;
      }
      this.axios = new Axios({
        baseURL: config.dev ? (config.local ? network.apiUrl.local : network.apiUrl.dev) : network.apiUrl.prod,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      this.backgroundAxios = new Axios({
        baseURL: config.dev ? (config.local ? network.apiUrl.local : network.apiUrl.dev) : network.apiUrl.prod,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      this.axios.interceptors.request.use(
        (config) => {
          loadingInstances.value++;
          return config;
        },
        (error) => {
          return Promise.reject(error);
        }
      );
      this.axios.interceptors.response.use(
        (response) => {
          loadingInstances.value--;
          if (response.status >= 400) {
            return Promise.reject(response);
          }
          return response;
        },
        (error) => {
          loadingInstances.value--;
          return Promise.reject(error);
        }
      );
      this.backgroundAxios.interceptors.response.use(
        (response) => {
          if (response.status >= 400) {
            return Promise.reject(response);
          }
          return response;
        },
        (error) => {
          return Promise.reject(error);
        }
      );
      this.getExchageRates(eth.reactive.value.chainId);
    });
  }
  
  async getHounds(filters: HoundQueryDto): Promise<FindAllResponse<HoundModel>> {
    try {
      return JSON.parse((await this.axios.get('/hounds', {params: filters})).data);
    } catch {
        toast.error('Hounds coudn\'t be loaded');
      return new FindAllResponse<HoundModel>();
    }
  }

  async getOwnHounds(filters: HoundQueryDto): Promise<FindAllResponse<HoundModel>> {
    try {
      return JSON.parse((await this.axios.get('/hounds', {
        params: {
          ...filters,
          owner: eth.userAddress
        }
      })).data);
    } catch {
      toast.error('Hounds couldn\'t be loaded');
      return new FindAllResponse<HoundModel>();
    }
  }

  async getHound(id: string): Promise<HoundModel> {
    try {
      return JSON.parse((await this.axios.get(`/hounds/${id}`)).data);
    } catch {
      toast.error('Hound coudn\'t be loaded');
      return {} as HoundModel;
    }
  }  

  async getQueues(filters: QueueQueryDto): Promise<FindAllResponse<QueueModel>> {
    try {
      return JSON.parse((await this.axios.get(`/queues`, {
        params: {
          ...filters,
          maxTicketPrice: maxTicketPrice.value,
          minTicketPrice: minTicketPrice.value,
          closed: false
        }
      })).data);
    } catch {
      toast.error('Queues couldn\'t be loaded');
      return new FindAllResponse<QueueModel>();
    }
  }

  async getQueue(id: string, hideLoading = false): Promise<QueueModel> {
    try {
      return JSON.parse((await (hideLoading ? this.backgroundAxios : this.axios).get(`/queues/${id}`)).data);
    } catch {
      toast.error('Queue coudn\'t be loaded');
    }
  }

  async getRaces(filters: RaceQueryDto): Promise<FindAllResponse<RaceModel>> {
    try {
      return JSON.parse((await this.axios.get(`/races`, {
        params: {
          ...filters,
          owner: filters.owner ? eth.userAddress : undefined,
          maxTicketPrice: maxTicketPrice.value,
          minTicketPrice: minTicketPrice.value,
        }
      })).data);
    } catch {
      toast.error('Races couldn\'t be loaded');
      return new FindAllResponse<RaceModel>();
    }
  }
  
  async getRaceParticipants(id: string) {
    return await this.axios.get(`/races/${id}/participants`);
  }

  async getArenas(filters: ArenaQueryDto): Promise<FindAllResponse<ArenaModel>> {
    try {
      return JSON.parse((await this.axios.get('/arenas', {
        params: {
          ...filters,
          owner: filters.owner ? eth.userAddress : undefined
        }
      })).data);
    } catch {
      toast.error('Arenas couldn\'t be loaded');
      return new FindAllResponse<ArenaModel>();
    }
  }

  async getParticipantValues(): Promise<number[]> {
    try {
      const queueParticipantCountValues = JSON.parse((await this.axios.get('/queues/participant-values')).data);
      const raceParticipantCountValues = JSON.parse((await this.axios.get('/races/participant-values')).data);
      return [...new Set([...queueParticipantCountValues, ...raceParticipantCountValues])] as number[];
    } catch(err) {
      console.log(err);
      return [0];
    }
  };

  async updatePackName(updateNameBody: { owner: string, signedMsg: string, pack_name: string }): Promise<PreferencesModel> {
    return JSON.parse((await this.axios.post('/preferences/update-pack-name', JSON.stringify(updateNameBody))).data);
  }

  async getUserPreferences(owner: string): Promise<PreferencesModel> {
    return JSON.parse((await this.axios.get(`/preferences/${owner}`)).data);
  }

  async getExchageRates(chainId: number) {
    try {
      const ratesData = JSON.parse((await this.backgroundAxios.get(`/externals/exchange-rates/${chainId}`)).data);
      exchangeRates.value = ratesData;
      exchangeRatesAvailable.value = true;
    } catch {
      exchangeRatesAvailable.value = false;
    }
    setTimeout(() => {
      this.getExchageRates(chainId);
    }, 30000);
  }
  
  async unstoppableReverseResolution(address: string): Promise<{
    "meta": {
      "domain": string,
      "blockchain": string,
      "networkId": number,
      "owner": string,
      "resolver": string,
      "registry": string,
      "reverse": boolean
    },
    "records": {
      "crypto.ETH.address": string
    }
  }> {
    loadingInstances.value++;
    try {
      return JSON.parse((await this.axios.get(`/unstoppable/reverse-resolution?address=${address}`)).data as string);
    } catch(err) {
      // toast.error('Domain couldn\'t be loaded');
      console.log(err);
      return;
    } finally {
      loadingInstances.value--;
    }
  }


  getPerformanceIndicators() {
    return Promise.resolve({
      data: {
        globalROI: 2,
        profit: 0.035,
        loss: 0.020
      }
    })
    // return axios.get(`${url}/statistics/performance-indicators`);
  }

  getWinCashCoop() {
    return Promise.resolve({
      firstPlace: 1.743,
      secondPlace: 3.213,
      thirdPlace: 0.034
    });
    // return axios.get(`${url}/statistics/win-cash-coop`);
  }

  async getRacingLeaderboard(take = 50, skip) {
    return JSON.parse((await this.axios.get(`/leaderboard/racing/take=${take}/skip=${skip}`)).data);
  }

  async getBreedingLeaderboard(take = 50, skip) {
    return JSON.parse((await this.axios.get(`/leaderboard/breeding/take=${take}/skip=${skip}`)).data);
  }
}

export const http = new HttpService();